import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Drawer from "@material-ui/core/Drawer";
import TextField from "@material-ui/core/TextField";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import MyLocationOutlinedIcon from "@material-ui/icons/MyLocationOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { batch, connect } from "react-redux";
import { checkoutV2Api } from "../api";
import { buildCheckoutLines, createCartFingerprint } from "../domain/cartContext";
import { sortContextBranches } from "../domain/contextBranches";
import { moneyToMajor } from "../domain/money";
import { getCheckoutV2Compatibility, isEnabledFlag } from "../domain/featureFlags";
import { formatAsapTimeParts, formatAsapTimeSummary, formatCheckoutTime, formatCheckoutTimeSummary, formatShortFulfillmentDate, getAsapWithinMinutes } from "../domain/timeSummary";
import {
  createFulfillmentFingerprint,
  getFulfillmentIssues,
  invalidateAddressForLocationChange,
  normalizeFulfillmentContext,
} from "../domain/fulfillmentContext";
import {
  commitCheckoutV2Context,
  setCheckoutV2CartValidation,
} from "../state/actions";
import {
  recordStorefrontV2Telemetry,
  STOREFRONT_V2_TELEMETRY,
} from "../telemetry";
import { fetchDeliveryCharges, loadBranchDetails } from "../../store/actions/store";
import { calculate_prep_time } from "../../store/reducers/cart";
import Money from "./Money";
import CartChangePreview from "./CartChangePreview";
import { cartChangeSummary } from "../domain/cartChangeNotices";
import DeferredFeedback from "./DeferredFeedback";
import ContextBranchDetails, { formatPickupBranchAddress } from "./ContextBranchDetails";
import ContextCountryChoices from "./ContextCountryChoices";
import "./ContextSheet.css";
import "../styles/typography.css";
import StorefrontActionButton from "../../Components/Storefront/ActionButton/StorefrontActionButton";

const RECENT_AREA_LIMIT = 3;
const MODE_COLLAPSE_SCROLL_TOP = 48;
const MODE_EXPAND_SCROLL_TOP = 8;

const AsapTimeLabel = ({ time, language, presentation }) => {
  const [dayOrEstimate, timeWindow] = formatAsapTimeParts(time, language, undefined, presentation);
  return (
    <>
      <span className="context-sheet__asap-day">{dayOrEstimate}</span>
      {timeWindow && <> <bdi className="context-sheet__asap-window" dir="ltr">{timeWindow}</bdi></>}
    </>
  );
};

const finiteCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const locationFor = (item = {}) => ({
  lat: finiteCoordinate(
    item.lat ?? item.latitude ?? item.area_lat ?? item.location_lat,
  ),
  lng: finiteCoordinate(
    item.lng ?? item.lon ?? item.longitude ?? item.area_lng ?? item.location_lng,
  ),
});

export const getContextDistanceKm = (from, item) => {
  const origin = locationFor(from);
  const destination = locationFor(item);
  if (
    origin.lat === null ||
    origin.lng === null ||
    destination.lat === null ||
    destination.lng === null
  )
    return null;
  const radians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = radians(destination.lat - origin.lat);
  const deltaLng = radians(destination.lng - origin.lng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(radians(origin.lat)) *
      Math.cos(radians(destination.lat)) *
      Math.sin(deltaLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const findNearestContextOption = (options = [], position = {}) =>
  (options || []).reduce((nearest, option) => {
    const distance = getContextDistanceKm(position, option);
    if (distance === null) return nearest;
    if (!nearest || distance < nearest.distance) return { option, distance };
    return nearest;
  }, null);

const recentAreaStorageKey = (storeKey) =>
  `storefront-v2-recent-areas:${storeKey || "store"}`;

export const getRecentContextAreaIds = (storage, storeKey) => {
  if (!storage || typeof storage.getItem !== "function") return [];
  try {
    const parsed = JSON.parse(storage.getItem(recentAreaStorageKey(storeKey)) || "[]");
    return Array.isArray(parsed)
      ? parsed.filter((areaId) => areaId !== null && areaId !== undefined).slice(0, RECENT_AREA_LIMIT)
      : [];
  } catch (error) {
    return [];
  }
};

export const rememberRecentContextArea = (storage, storeKey, areaId) => {
  if (
    !storage ||
    typeof storage.setItem !== "function" ||
    areaId === null ||
    areaId === undefined
  )
    return [];
  const recent = getRecentContextAreaIds(storage, storeKey).filter(
    (candidate) => !matches(candidate, areaId),
  );
  const next = [areaId, ...recent].slice(0, RECENT_AREA_LIMIT);
  try {
    storage.setItem(recentAreaStorageKey(storeKey), JSON.stringify(next));
  } catch (error) {
    return recent;
  }
  return next;
};

export const cloneContext = (context = {}) => ({
  ...context,
  address: { ...(context.address || {}) },
  time: { ...(context.time || {}) },
  vehicle: { ...(context.vehicle || {}) },
  qr: { ...(context.qr || {}) },
});

const id = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  return /^\d+$/.test(`${value}`) ? Number(value) : value;
};

const optionLabel = (item, arabic) =>
  (arabic &&
    (item.ar_name || item.name_ar || item.arabic_name || item.title_ar)) ||
  item.name ||
  item.title ||
  item.branch_name ||
  `${item.id}`;

const matches = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  `${left}` === `${right}`;

const defaultContextOptionFilter = createFilterOptions();
export const CONTEXT_OPTION_PAGE_SIZE = 80;

export const filterContextSelectOptions = (
  options,
  filterState,
  selected,
  arabic = false,
) => {
  const inputValue = `${filterState?.inputValue || ""}`.trim();
  const selectedLabel = selected ? optionLabel(selected, arabic).trim() : "";
  // MUI normally filters an opened Autocomplete by the selected label. That
  // makes every other area appear to disappear until the customer manually
  // clears the field. Reopening a selected value should show the full list;
  // typing a different value still searches normally.
  if (selectedLabel && inputValue === selectedLabel) return options;
  return defaultContextOptionFilter(options, filterState);
};

const getChargeAreaId = (charge = {}) =>
  charge.area_id ??
  charge.delivery_charge__area_id ??
  charge.delivery_charge__area__id ??
  null;

const getChargeBranchId = (charge = {}) =>
  charge.branch ??
  charge.branch_id ??
  charge.branch_delivery_charge__branch__id ??
  null;

const getChargeCountryId = (charge = {}) =>
  charge.country_id ??
  charge.country ??
  charge.delivery_charge__area__country_id ??
  null;

// Branch-based second-load responses intentionally keep `areas` compact and
// carry the selectable area data on each BDC row. Normalize both shapes into
// one deduplicated list for F1 without changing the Redux/API contract.
export const deriveContextAreas = (
  areas = [],
  branchDeliveryCharges = [],
) => {
  const byId = new Map();
  (areas || []).forEach((area) => {
    if (area?.id === null || area?.id === undefined) return;
    byId.set(`${area.id}`, area);
  });
  (branchDeliveryCharges || []).forEach((charge) => {
    const areaId = getChargeAreaId(charge);
    if (areaId === null || areaId === undefined) return;
    const key = `${areaId}`;
    const current = byId.get(key) || {};
    const provinceParts = `${charge.province || ""}`.split(",");
    byId.set(key, {
      ...current,
      id: areaId,
      name:
        current.name ||
        charge.area ||
        charge.delivery_charge__area__name ||
        `${areaId}`,
      ar_name:
        current.ar_name ||
        current.name_ar ||
        charge.area_ar ||
        charge.delivery_charge__area__ar_name ||
        "",
      country:
        current.country ?? current.country_id ?? getChargeCountryId(charge),
      province:
        current.province ?? current.province_id ?? charge.province_id ?? null,
      province_name:
        current.province_name ||
        charge.province_en ||
        charge.province_name ||
        (provinceParts[0] !== "-" ? provinceParts[0] : "") ||
        "",
      province_ar_name:
        current.province_ar_name ||
        charge.province_ar ||
        (provinceParts[1] !== "-" ? provinceParts[1] : "") ||
        "",
      lat: current.lat ?? charge.area_lat ?? null,
      lng: current.lng ?? charge.area_lng ?? null,
    });
  });
  return Array.from(byId.values());
};

export const getMissingContextOptionGroups = (
  props = {},
  draft = {},
) => {
  const settings = props.settings || {};
  const countries = props.countries || [];
  const branches = props.branches || [];
  const branchDeliveryCharges = props.branchDeliveryCharges || [];
  const areas = deriveContextAreas(props.areas || [], branchDeliveryCharges);
  const needsDelivery =
    draft.type === "delivery" ||
    (!draft.type && settings.enable_delivery !== false);
  const needsPickup =
    draft.type === "pickup" ||
    (!draft.type && settings.enable_pickup !== false);
  const missing = [];

  if (needsDelivery) {
    if (!countries.length) missing.push("countries");
    if (!areas.length) missing.push("areas");
    if (settings.is_branch_based_ordering) {
      if (!branches.length) missing.push("branches");
      if (!branchDeliveryCharges.length)
        missing.push("branch_delivery_charges");
    }
  }
  if (needsPickup && !branches.length) missing.push("branches");
  return Array.from(new Set(missing));
};

export const isContextSlotEligible = (day, slot) =>
  day?.active === true && slot?.eligible !== false;

// The checkout API identifies a slot by date/start/end, not by a row ID.
// Repeated rows for the same window cannot represent distinct choices.
export const getContextDisplaySlots = (day) => (day?.slots || []).filter((slot, index, slots) =>
  slots.findIndex(other => other.start === slot.start && other.end === slot.end) === index);

export const getContextSlotReason = (day, slot, language = "english") => {
  const reason = slot?.reason || day?.reason;
  if (!reason || typeof reason !== "object") return "";
  return (
    reason[language] ||
    reason.labels?.[language] ||
    reason.english ||
    reason.labels?.english ||
    reason.code ||
    ""
  );
};

export const getContextAsapLabel = formatAsapTimeSummary;

export const hasSelectedFulfillmentMode = (draft = {}) =>
  draft.type === "delivery" || draft.type === "pickup";

export const getContextModeAvailability = (
  settings = {},
  branches = [],
) => ({
  // Legacy TURN_ON_MENU temporarily flips enable_pickup off after a delivery
  // area is chosen. That flag controls the old browsing mode, not whether the
  // store actually supports pickup. Explicit branch capabilities remain the
  // durable source for switching modes inside checkout.
  delivery:
    settings.enable_delivery !== false ||
    branches.some((branch) => branch.enable_delivery === true),
  pickup:
    settings.enable_pickup !== false ||
    branches.some((branch) => branch.enable_pickup === true),
});

export const getEligibleContextBranches = ({
  branches = [],
  branchDeliveryCharges = [],
  type,
  areaId,
  isBranchBased = false,
}) => {
  const hasBranchTaggedCharges = branchDeliveryCharges.some(
    (charge) => getChargeBranchId(charge) !== null,
  );
  return branches.filter((branch) => {
    const supportsMode =
      type === "pickup"
        ? branch.enable_pickup !== false
        : branch.enable_delivery !== false;
    if (
      !supportsMode ||
      type !== "delivery" ||
      !isBranchBased ||
      !areaId ||
      !hasBranchTaggedCharges
    )
      return supportsMode;
    return branchDeliveryCharges.some(
      (charge) =>
        matches(getChargeBranchId(charge), branch.id) &&
        matches(getChargeAreaId(charge), areaId),
    );
  });
};

const findDeliveryChargeId = (
  branchDeliveryCharges = [],
  branchId,
  areaId,
) => {
  const charge = branchDeliveryCharges.find(
    (item) =>
      matches(getChargeBranchId(item), branchId) &&
      (!areaId ||
        getChargeAreaId(item) === null ||
        matches(getChargeAreaId(item), areaId)),
  );
  return charge?.id || charge?.branch_delivery_charge_id || null;
};

export const getContextAreaCommerce = (
  areaId,
  branchDeliveryCharges = [],
  areas = [],
  pricingContext = {},
) => {
  const charges = (branchDeliveryCharges || []).filter((charge) =>
    matches(getChargeAreaId(charge), areaId),
  );
  const finiteValues = (keys) =>
    charges
      .map((charge) => {
        const key = keys.find(
          (candidate) => charge[candidate] !== null && charge[candidate] !== undefined,
        );
        const value = Number(key ? charge[key] : NaN);
        return Number.isFinite(value) && value >= 0 ? value : null;
      })
      .filter((value) => value !== null);
  const fees = finiteValues(["price", "delivery_fee", "delivery_charge"]);
  const minimums = finiteValues([
    "minimum_order_value",
    "minimum_order",
    "mov",
  ]);
  const area = (areas || []).find((candidate) => matches(candidate.id, areaId));
  const { settings = {}, baseCountryId, countries = [] } = pricingContext;
  const countryId = area?.country?.id ?? area?.country ?? area?.country_id;
  const compatibility = getCheckoutV2Compatibility(settings, {
    context: { type: "delivery", countryId },
    store: { base_country_id: baseCountryId },
  });
  const baseCountry = countries.find((country) => matches(country.id, baseCountryId));
  const domesticAramex = isEnabledFlag(settings.enable_aramex) &&
    baseCountry?.alpha_2_code === "SA" && matches(countryId, baseCountryId);
  // Branch/area prices are not shipping quotes. The cart/checkout authority
  // calculates weight-based rates (including ShippingCustomPricing).
  // Legacy storefront serializers omit enable_dispatching. In that case the
  // exposed provider flags are sufficient to avoid advertising a false rate.
  const feePending = (settings.enable_dispatching === undefined || isEnabledFlag(settings.enable_dispatching)) &&
    ((compatibility.dynamicDeliveryProvider && compatibility.internationalDelivery) || domesticAramex);
  const areaFee = Number(area?.price ?? area?.delivery_fee);
  const areaMinimum = Number(
    area?.minimum_order_value ?? area?.minimun_order_value ?? area?.minimum_order,
  );
  return {
    ...(feePending ? { feePending: true } : {}),
    fee: feePending ? null : fees.length
      ? Math.min(...fees)
      : Number.isFinite(areaFee) && areaFee >= 0
        ? areaFee
        : null,
    minimum: minimums.length
      ? Math.min(...minimums)
      : Number.isFinite(areaMinimum) && areaMinimum >= 0
        ? areaMinimum
        : null,
    varies: new Set(fees).size > 1 || new Set(minimums).size > 1,
  };
};

export const getSelectedContextAreaCommerce = (
  area,
  branchDeliveryChargeId,
  branchDeliveryCharges = [],
  pricingContext = {},
) => {
  if (!area) return { fee: null, minimum: null, varies: false };
  const selectedCharge = (branchDeliveryCharges || []).find(
    (charge) =>
      matches(
        charge.id ?? charge.branch_delivery_charge_id,
        branchDeliveryChargeId,
      ) && matches(getChargeAreaId(charge), area.id),
  );
  return getContextAreaCommerce(
    area.id,
    selectedCharge ? [selectedCharge] : [],
    [area],
    pricingContext,
  );
};

export const getContextBranchState = (branch = {}, language = "english") => {
  const arabic = language === "arabic";
  const explicitlyClosed =
    branch.is_open === false ||
    branch.open_now === false ||
    branch.is_closed === true ||
    branch.closed === true;
  const busy =
    branch.busy_until_active === true || branch.branch_is_busy === true;
  const busyUntil = [branch.busy_until_date, branch.busy_until_time]
    .filter(Boolean)
    .join(" · ");
  if (explicitlyClosed)
    return { open: false, label: arabic ? "مغلق حالياً" : "Closed now" };
  if (busy)
    return {
      open: false,
      label: busyUntil
        ? arabic
          ? `مشغول حتى ${busyUntil}`
          : `Busy until ${busyUntil}`
        : arabic
          ? "مشغول حالياً"
          : "Busy right now",
    };
  if (branch.is_open === true || branch.open_now === true)
    return { open: true, label: arabic ? "مفتوح الآن" : "Open now" };
  return {
    open: true,
    label: arabic ? "متاح للطلب" : "Available for ordering",
  };
};

export const groupContextAreas = (areas = [], language = "english") => {
  const arabic = language === "arabic";
  const groups = new Map();
  (areas || []).forEach((area) => {
    const label =
      (arabic &&
        (area.province_ar_name || area.province_name_ar || area.governorate_ar)) ||
      area.province_name ||
      area.governorate ||
      (arabic ? "مناطق أخرى" : "Other areas");
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(area);
  });
  return Array.from(groups.entries())
    .map(([label, options]) => ({
      label,
      options: [...options].sort((left, right) =>
        optionLabel(left, arabic).localeCompare(optionLabel(right, arabic)),
      ),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const getNearestServedContextArea = (
  selectedArea,
  areas = [],
  branchDeliveryCharges = [],
) => {
  const served = (areas || []).filter(
    (area) =>
      !matches(area.id, selectedArea?.id) &&
      (area.served === true ||
        branchDeliveryCharges.some((charge) =>
          matches(getChargeAreaId(charge), area.id),
        )),
  );
  if (!served.length) return null;
  const nearest = selectedArea
    ? findNearestContextOption(served, selectedArea)
    : null;
  return (
    nearest?.option ||
    [...served].sort((left, right) =>
      optionLabel(left, false).localeCompare(optionLabel(right, false)),
    )[0]
  );
};

export const getAutomaticContextChanges = (props = {}, draft = {}) => {
  const modes = getContextModeAvailability(props.settings, props.branches);
  const enabledModes = ["delivery", "pickup"].filter((type) => modes[type]);
  if (enabledModes.length === 1 && draft.type !== enabledModes[0]) {
    const type = enabledModes[0];
    return {
      type,
      isPickup: type === "pickup",
      pickupMode: type === "pickup" ? "inside" : null,
      areaId: null,
      branchId: null,
      branchDeliveryChargeId: null,
    };
  }
  if (draft.type !== "delivery") return null;
  const settings = props.settings || {};
  const countries = props.countries || [];
  const areas = deriveContextAreas(
    props.areas || [],
    props.branchDeliveryCharges || [],
  );
  const firstCountry = countries[0];
  const selectedCountry = countries.find((country) =>
    matches(country.id, draft.countryId),
  );
  const nextCountryId = selectedCountry?.id ?? firstCountry?.id ?? null;
  const changes = {};

  if (nextCountryId !== null && !matches(nextCountryId, draft.countryId))
    changes.countryId = nextCountryId;

  const selectedArea = areas.find((area) => matches(area.id, draft.areaId));
  const selectedAreaCountry =
    selectedArea?.country ?? selectedArea?.country_id ?? null;
  const areaMatchesCountry =
    selectedArea &&
    (!nextCountryId ||
      !selectedAreaCountry ||
      matches(selectedAreaCountry, nextCountryId));
  const areaOptionsKnown = areas.length > 0;
  const nextAreaId =
    !areaOptionsKnown || areaMatchesCountry ? draft.areaId : null;
  if (areaOptionsKnown && draft.areaId && !areaMatchesCountry) {
    changes.areaId = null;
    changes.branchId = null;
    changes.branchDeliveryChargeId = null;
  }

  if (!settings.is_branch_based_ordering || !nextAreaId)
    return Object.keys(changes).length ? changes : null;

  if (
    !(props.branches || []).length ||
    !(props.branchDeliveryCharges || []).length
  )
    return Object.keys(changes).length ? changes : null;

  const eligibleBranches = getEligibleContextBranches({
    branches: props.branches || [],
    branchDeliveryCharges: props.branchDeliveryCharges || [],
    type: "delivery",
    areaId: nextAreaId,
    isBranchBased: true,
  });
  // Delivery is area-led. The shopper should not need to understand the
  // merchant's branch topology, so always bind the highest-priority eligible
  // branch (the input is already stably sorted by branch priority).
  const automaticBranch = eligibleBranches[0];

  if (!automaticBranch) {
    if (draft.branchId) changes.branchId = null;
    if (draft.branchDeliveryChargeId)
      changes.branchDeliveryChargeId = null;
    return Object.keys(changes).length ? changes : null;
  }

  const deliveryChargeId = findDeliveryChargeId(
    props.branchDeliveryCharges,
    automaticBranch.id,
    nextAreaId,
  );
  if (!matches(automaticBranch.id, draft.branchId))
    changes.branchId = automaticBranch.id;
  if (!matches(deliveryChargeId, draft.branchDeliveryChargeId))
    changes.branchDeliveryChargeId = deliveryChargeId;
  return Object.keys(changes).length ? changes : null;
};

export const getRecommendedContextTime = (slots = {}) => {
  if (slots.asap && slots.asap.eligible !== false)
    return { type: "asap", value: slots.asap };
  const scheduled = slots.pickup || slots.scheduled || {};
  const dates = Object.keys(scheduled).sort();
  for (const date of dates) {
    const day = scheduled[date];
    const available = getContextDisplaySlots(day)
      .filter((slot) => isContextSlotEligible(day, slot))
      .sort((left, right) =>
        `${left.start || ""}-${left.end || ""}`.localeCompare(
          `${right.start || ""}-${right.end || ""}`,
        ),
      );
    if (available.length)
      return { type: "scheduled", date, value: available[0] };
  }
  return null;
};

export const formatContextSlotDate = formatShortFulfillmentDate;

export const getContextErrorMessage = (error, arabic = false) => {
  const code = typeof error === "object" ? error?.code : error;
  const messages = {
    delivery_slot_unavailable: ["This time slot is no longer available. Refresh the times and choose another slot.", "هذا الموعد لم يعد متاحاً. حدّث المواعيد واختر موعداً آخر."],
    capacity_full: ["This time slot is fully booked. Choose another time.", "هذا الموعد مكتمل الحجز. اختر وقتاً آخر."],
    date_capacity_full: ["This day is fully booked. Choose another day.", "هذا اليوم مكتمل الحجز. اختر يوماً آخر."],
    store_closed: ["The store is closed at the selected time. Choose another day or time.", "المتجر مغلق في الموعد المختار. اختر يوماً أو وقتاً آخر."],
    asap_unavailable: ["ASAP is not available for this location. Choose a scheduled time.", "أقرب وقت غير متاح لهذا الموقع. اختر موعداً مجدولاً."],
    scheduled_time_unavailable: ["Scheduled orders are not available for this location. Choose another location or ASAP if available.", "الطلبات المجدولة غير متاحة لهذا الموقع. اختر موقعاً آخر أو أقرب وقت إن كان متاحاً."],
    delivery_time_required: ["Choose a day and time to continue.", "اختر اليوم والوقت للمتابعة."],
    booking_slot_unavailable: ["A booking time is no longer available. Edit the booking in your cart and choose another time.", "موعد الحجز لم يعد متاحاً. عدّل الحجز في السلة واختر موعداً آخر."],
    delivery_times_load_failed: ["We couldn’t load delivery times. Check your connection and try again.", "تعذر تحميل مواعيد التوصيل. تحقق من اتصالك وحاول مرة أخرى."],
    fulfillment_method_required: ["Choose delivery or pickup to continue.", "اختر التوصيل أو الاستلام للمتابعة."],
    delivery_disabled: ["Delivery is currently disabled. Choose pickup if available.", "التوصيل غير متاح حالياً. اختر الاستلام إن كان متاحاً."],
    pickup_disabled: ["Pickup is currently disabled. Choose delivery if available.", "الاستلام غير متاح حالياً. اختر التوصيل إن كان متاحاً."],
    branch_unavailable: ["The selected branch is no longer available. Choose another location.", "الفرع المختار لم يعد متاحاً. اختر موقعاً آخر."],
    branch_offline: ["The selected branch is not accepting orders right now. Choose another location.", "الفرع المختار لا يستقبل طلبات حالياً. اختر موقعاً آخر."],
    branch_hidden: ["The selected branch is no longer available for online orders. Choose another location.", "الفرع المختار لم يعد متاحاً للطلبات عبر الإنترنت. اختر موقعاً آخر."],
    delivery_area_unavailable: ["Delivery is not available to the selected area. Choose another area.", "التوصيل غير متاح للمنطقة المختارة. اختر منطقة أخرى."],
    branch_delivery_area_mismatch: ["The selected branch does not deliver to this area. Select the area again to update the branch.", "الفرع المختار لا يوصل إلى هذه المنطقة. أعد اختيار المنطقة لتحديث الفرع."],
    branch_delivery_disabled: ["The selected branch does not offer delivery. Choose another location or pickup.", "الفرع المختار لا يقدم خدمة التوصيل. اختر موقعاً آخر أو الاستلام."],
    pickup_branch_required: ["Choose a pickup branch to continue.", "اختر فرع الاستلام للمتابعة."],
    branch_pickup_disabled: ["Pickup is not available at this branch. Choose another branch.", "الاستلام غير متاح في هذا الفرع. اختر فرعاً آخر."],
    country_context_mismatch: ["The selected area or branch does not match the country. Select your country and location again.", "المنطقة أو الفرع لا يتطابق مع الدولة المختارة. أعد اختيار الدولة والموقع."],
    car_pickup_unavailable: ["Car pickup is not available here. Choose collection inside the store.", "الاستلام من السيارة غير متاح هنا. اختر الاستلام من داخل الفرع."],
    car_make_required: ["Enter your car make to continue.", "أدخل نوع السيارة للمتابعة."],
    car_color_required: ["Enter your car color to continue.", "أدخل لون السيارة للمتابعة."],
    car_plate_required: ["Enter your car plate number to continue.", "أدخل رقم لوحة السيارة للمتابعة."],
    map_pin_coordinates_invalid: ["Your map pin is invalid. Set the delivery pin again in your address.", "موقعك على الخريطة غير صالح. أعد تحديد موقع التوصيل في عنوانك."],
    map_pin_validation_unavailable: ["We couldn’t verify your map pin. Try again or update the pin in your address.", "تعذر التحقق من موقعك على الخريطة. حاول مجدداً أو حدّث الموقع في عنوانك."],
    map_pin_delivery_area_mismatch: ["Your map pin is outside the selected delivery area. Update the area or the pin in your address.", "موقعك على الخريطة خارج منطقة التوصيل المختارة. حدّث المنطقة أو الموقع في عنوانك."],
    delivery_not_applicable: ["The items in your cart do not support delivery. Choose pickup or update your cart.", "منتجات سلتك لا تدعم التوصيل. اختر الاستلام أو حدّث السلة."],
    storefront_checkout_v2_disabled: ["The store’s checkout settings changed. Refresh the page to continue.", "تغيّرت إعدادات إتمام الطلب في المتجر. حدّث الصفحة للمتابعة."],
    fulfillment_type: [
      "Choose delivery or pickup to continue.",
      "اختر التوصيل أو الاستلام للمتابعة.",
    ],
    country: ["Choose a country.", "اختر الدولة."],
    area: ["Choose a delivery area.", "اختر منطقة التوصيل."],
    branch: ["Choose a branch.", "اختر الفرع."],
    branch_delivery_charge: [
      "That branch does not currently deliver to this area.",
      "هذا الفرع لا يوصل إلى المنطقة المختارة حالياً.",
    ],
    fulfillment_time: [
      "Choose an available time.",
      "اختر موعداً متاحاً.",
    ],
    delivery_time_unavailable: [
      "No times are available for this location right now. Try another option.",
      "لا توجد مواعيد متاحة لهذا الموقع حالياً. جرّب خياراً آخر.",
    ],
    context_options_unavailable: [
      "We couldn’t load the available locations. Try again.",
      "تعذر تحميل المواقع المتاحة. حاول مرة أخرى.",
    ],
    context_unavailable: [
      "We couldn’t check this selection. Please try again.",
      "تعذر التحقق من هذا الاختيار. حاول مرة أخرى.",
    ],
    asap_authority_expired: [
      "Refreshing the ASAP estimate…",
      "جاري تحديث تقدير أقرب وقت…",
    ],
    asap_authority_unavailable: [
      "That ASAP estimate expired. We’re refreshing the available times.",
      "انتهت صلاحية تقدير أقرب وقت. سنحدّث المواعيد المتاحة.",
    ],
    asap_context_changed: [
      "The delivery location changed. Choose the best time again.",
      "تغيّر موقع الطلب. اختر أفضل موعد مرة أخرى.",
    ],
    asap_window_mismatch: [
      "That ASAP estimate changed. Choose the refreshed time.",
      "تغيّر تقدير أقرب وقت. اختر الموعد المحدّث.",
    ],
    asap_cart_changed: [
      "Your cart changed. We’re refreshing the available times.",
      "تغيّرت سلتك. سنحدّث المواعيد المتاحة.",
    ],
    delivery_time_expired: [
      "That time just passed. Choose the refreshed time.",
      "انقضى هذا الموعد للتو. اختر الموعد المحدّث.",
    ],
    delivery_time_invalid: [
      "Choose another delivery or pickup time.",
      "اختر موعد توصيل أو استلام آخر.",
    ],
    context_invalid: [
      "This option is not available for the current cart.",
      "هذا الخيار غير متاح للسلة الحالية.",
    ],
  };
  const serverMessage = typeof error === "object" && (arabic ? error?.arabic || error?.message || error?.english : error?.english || error?.message);
  if (serverMessage && typeof serverMessage === "string") return serverMessage;
  if (messages[code]) return messages[code][arabic ? 1 : 0];
  if (typeof code === "string" && /\s/.test(code)) return code;
  const fallback = arabic ? "تعذر التحقق من خيارات الطلب. حاول مجدداً أو تواصل مع المتجر واذكر المرجع:" : "We couldn’t validate your order options. Try again, or contact the store with this reference:";
  return `${fallback} ${code || "context_unavailable"}`;
};

export class ContextSheet extends Component {
  static defaultProps = {
    api: checkoutV2Api,
    areas: [],
    branches: [],
    branchDeliveryCharges: [],
    countries: [],
    telemetry: recordStorefrontV2Telemetry,
  };

  state = {
    draft: cloneContext(this.props.initialDraft || this.props.context),
    preview: null,
    previewKey: null,
    slots: null,
    showSlots: false,
    selectedSlotDate: "",
    slotsLoading: false,
    optionsLoading: false,
    optionsError: "",
    locating: false,
    locationError: "",
    userPosition: null,
    recentAreaIds: [],
    loading: false,
    error: "",
    modeCollapsed: false,
    branchDetailsOpen: false,
    searchOpen: false,
    searchKey: null,
    searchText: "",
    searchOptionLimit: CONTEXT_OPTION_PAGE_SIZE,
    mobileViewport: null,
  };
  mounted = false;
  previewRequestId = 0;
  slotsRequestId = 0;
  slotsInFlightRequestId = null;
  optionsRequestId = 0;
  geolocationRequestId = 0;
  optionsHydrationAttempts = new Set();
  slotsRef = React.createRef();
  slotDaysRef = React.createRef();
  bodyRef = React.createRef();
  activeSearchField = null;
  searchLayoutFrame = null;

  componentDidMount() {
    this.mounted = true;
    this.viewport = window.visualViewport;
    if (this.viewport) {
      this.viewport.addEventListener("resize", this.updateSearchViewport);
      this.viewport.addEventListener("scroll", this.updateSearchViewport);
    }
    window.addEventListener("resize", this.updateSearchViewport);
    this.updateSearchViewport();
    this.loadRecentAreas();
    this.applyAutomaticDefaults();
    this.ensureContextOptions({ force: true });
    this.maybeLoadSlots();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.open && this.props.open) {
      this.previewRequestId += 1;
      this.slotsRequestId += 1;
      this.optionsRequestId += 1;
      this.geolocationRequestId += 1;
      this.optionsHydrationAttempts.clear();
      this.setState({
        draft: cloneContext(this.props.initialDraft || this.props.context),
        preview: null,
        previewKey: null,
        slots: null,
        showSlots: false,
        selectedSlotDate: "",
        slotsLoading: false,
        optionsLoading: false,
        optionsError: "",
        locating: false,
        locationError: "",
        userPosition: null,
        recentAreaIds: [],
        loading: false,
        error: "",
        modeCollapsed: false,
        branchDetailsOpen: false,
        searchOpen: false,
        searchKey: null,
        searchText: "",
      }, () => {
        this.loadRecentAreas();
        this.applyAutomaticDefaults();
        this.ensureContextOptions({ force: true });
        this.maybeLoadSlots();
      });
      return;
    }
    if (prevProps.open && !this.props.open) {
      this.closeSearch();
      this.optionsRequestId += 1;
      this.geolocationRequestId += 1;
      if (this.state.optionsLoading)
        this.setState({ optionsLoading: false });
      return;
    }
    if (!this.props.open) return;

    if (this.applyAutomaticDefaults()) return;

    const missingOptions = getMissingContextOptionGroups(
      this.props,
      this.state.draft,
    );
    if (
      !missingOptions.length &&
      (this.state.optionsLoading || this.state.optionsError)
    ) {
      this.setState({ optionsLoading: false, optionsError: "" });
      return;
    }
    if (
      prevState?.draft?.type !== this.state.draft.type ||
      prevProps.countryCode !== this.props.countryCode ||
      prevProps.countries !== this.props.countries ||
      prevProps.areas !== this.props.areas ||
      prevProps.branches !== this.props.branches ||
      prevProps.branchDeliveryCharges !== this.props.branchDeliveryCharges
    )
      this.ensureContextOptions();
    this.maybeLoadSlots();
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.viewport) {
      this.viewport.removeEventListener("resize", this.updateSearchViewport);
      this.viewport.removeEventListener("scroll", this.updateSearchViewport);
    }
    window.removeEventListener("resize", this.updateSearchViewport);
    if (this.searchLayoutFrame !== null) cancelAnimationFrame(this.searchLayoutFrame);
    this.previewRequestId += 1;
    this.slotsRequestId += 1;
    this.optionsRequestId += 1;
    this.geolocationRequestId += 1;
  }

  updateSearchViewport = () => {
    if (!this.mounted) return;
    // Mobile keyboards resize the visual viewport, not necessarily vh/dvh.
    // Ignore pinch zoom so zooming never reshapes the drawer underneath a user.
    const viewport = this.viewport || window.visualViewport;
    const mobileViewport = window.innerWidth <= 600 && (!viewport || viewport.scale === 1)
      ? { height: Math.round(viewport?.height || window.innerHeight), top: Math.round(viewport?.offsetTop || 0) }
      : null;
    if (mobileViewport?.height !== this.state.mobileViewport?.height ||
        mobileViewport?.top !== this.state.mobileViewport?.top) {
      this.setState({ mobileViewport }, this.positionSearch);
    } else this.positionSearch();
  };

  openSearch = (event) => {
    this.activeSearchField = event.currentTarget.closest(".context-sheet__field--search");
    this.setState({ searchOpen: true, searchOptionLimit: CONTEXT_OPTION_PAGE_SIZE }, this.positionSearch);
  };

  loadMoreSearchOptions = () => {
    const total = this.searchResults?.length || 0;
    this.setState(state => state.searchOptionLimit < total
      ? { searchOptionLimit: Math.min(total, state.searchOptionLimit + CONTEXT_OPTION_PAGE_SIZE) }
      : null);
  };

  closeSearch = () => {
    const field = this.activeSearchField;
    this.activeSearchField = null;
    // Collapsing MUI's popup leaves its input focused. On touch devices that
    // also leaves the software keyboard open; blur only this search input.
    const touchViewport = window.innerWidth <= 600 || window.matchMedia?.("(pointer: coarse)").matches;
    if (this.mounted) this.setState({ searchOpen: false, searchKey: null, searchText: "" }, () => {
      // Run after the popup click bubbles: MUI can refocus the input there.
      const focused = document.activeElement;
      if (touchViewport && field?.contains(focused) && focused?.tagName === "INPUT") focused.blur();
    });
  };

  positionSearch = () => {
    if (this.searchLayoutFrame !== null) cancelAnimationFrame(this.searchLayoutFrame);
    this.searchLayoutFrame = requestAnimationFrame(() => {
      this.searchLayoutFrame = null;
      const body = this.bodyRef.current;
      const field = this.activeSearchField;
      if (!this.mounted || !this.props.open || !this.state.searchOpen ||
          !this.state.mobileViewport || !body || !field) return;
      // Scroll only this drawer, not the storefront behind it. Extra search
      // padding lets even the last field reach the top of the visible body.
      body.scrollTop += field.getBoundingClientRect().top - body.getBoundingClientRect().top - 8;
      const input = field.querySelector(".MuiAutocomplete-inputRoot");
      const available = body.getBoundingClientRect().bottom - (input || field).getBoundingClientRect().bottom - 12;
      field.style.setProperty("--context-search-results-height", `${Math.max(80, available)}px`);
    });
  };

  handleBodyScroll = (event) => {
    if (!this.mounted || !this.props.open) return;
    const scrollTop = event.currentTarget.scrollTop;
    // Different entry/exit thresholds prevent a shrinking sticky selector
    // from toggling back and forth near the top (including touch overscroll).
    const modeCollapsed = this.state.modeCollapsed
      ? scrollTop > MODE_EXPAND_SCROLL_TOP
      : scrollTop >= MODE_COLLAPSE_SCROLL_TOP;
    if (modeCollapsed !== this.state.modeCollapsed)
      this.setState({ modeCollapsed });
  };

  getOptionsHydrationKey = () =>
    `${this.props.countryCode || "base"}:${this.state.draft.type || "initial"}`;

  getRecentAreaStoreKey = () =>
    this.props.storeKey ||
    this.props.settings?.store_id ||
    this.props.settings?.store_uuid ||
    (typeof window !== "undefined" && window.location?.hostname) ||
    this.props.countryCode;

  getStorage = () => {
    try {
      return typeof window !== "undefined" ? window.localStorage : null;
    } catch (error) {
      return null;
    }
  };

  loadRecentAreas = () => {
    const recentAreaIds = getRecentContextAreaIds(
      this.getStorage(),
      this.getRecentAreaStoreKey(),
    );
    if (this.mounted) this.setState({ recentAreaIds });
    return recentAreaIds;
  };

  rememberArea = (areaId) => {
    const recentAreaIds = rememberRecentContextArea(
      this.getStorage(),
      this.getRecentAreaStoreKey(),
      areaId,
    );
    if (this.mounted) this.setState({ recentAreaIds });
  };

  useMyLocation = () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      this.setState({ locationError: "location_unavailable" });
      return;
    }
    const requestId = ++this.geolocationRequestId;
    this.setState({ locating: true, locationError: "" });
    const handlePosition = (position) => {
      if (!this.mounted || requestId !== this.geolocationRequestId) return;
      if (
        !Number.isFinite(Number(position?.coords?.latitude)) ||
        !Number.isFinite(Number(position?.coords?.longitude))
      ) {
        this.setState({
          locating: false,
          locationError: "location_unavailable",
        });
        return;
      }
      const userPosition = {
        lat: Number(position.coords.latitude),
        lng: Number(position.coords.longitude),
      };
      if (this.state.draft.type === "pickup") {
        const pickupBranches = getEligibleContextBranches({
          branches: this.props.branches,
          type: "pickup",
        });
        const nearestBranch = findNearestContextOption(
          pickupBranches,
          userPosition,
        );
        if (!nearestBranch) {
          this.setState({
            locating: false,
            locationError: "location_branch_unavailable",
            userPosition,
          });
          return;
        }
        this.setState({ locating: false, locationError: "", userPosition });
        this.selectBranch(nearestBranch.option.id);
        return;
      }
      const contextAreas = deriveContextAreas(
        this.props.areas,
        this.props.branchDeliveryCharges,
      ).filter((area) => {
        const countryId = area.country || area.country_id;
        return (
          !this.state.draft.countryId ||
          !countryId ||
          matches(countryId, this.state.draft.countryId)
        );
      }).filter(
        (area) =>
          area.served !== false &&
          (!this.props.settings.is_branch_based_ordering ||
            this.props.branchDeliveryCharges.some((charge) =>
              matches(getChargeAreaId(charge), area.id),
            )),
      );
      const nearest = findNearestContextOption(contextAreas, userPosition);
      if (!nearest) {
        this.setState({
          locating: false,
          locationError: "location_area_unavailable",
          userPosition,
        });
        return;
      }
      this.setState({ locating: false, locationError: "", userPosition });
      this.selectArea(nearest.option.id, { capturedPosition: userPosition });
    };
    const finishWithError = (error) => {
      if (!this.mounted || requestId !== this.geolocationRequestId) return;
      this.setState({
        locating: false,
        locationError:
          Number(error?.code) === 1
            ? "location_denied"
            : "location_unavailable",
      });
    };
    const retryWithoutHighAccuracy = (error) => {
      if (!this.mounted || requestId !== this.geolocationRequestId) return;
      if (Number(error?.code) === 1) {
        finishWithError(error);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        handlePosition,
        finishWithError,
        { enableHighAccuracy: false, maximumAge: 900000, timeout: 20000 },
      );
    };
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      retryWithoutHighAccuracy,
      { enableHighAccuracy: true, maximumAge: 300000, timeout: 10000 },
    );
  };

  ensureContextOptions = async ({ force = false } = {}) => {
    if (
      !this.mounted ||
      !this.props.open ||
      typeof this.props.fetchContextOptions !== "function"
    )
      return null;
    const missing = getMissingContextOptionGroups(
      this.props,
      this.state.draft,
    );
    if (!force && !missing.length) return null;
    const attemptKey = this.getOptionsHydrationKey();
    if (this.optionsHydrationAttempts.has(attemptKey)) return null;
    this.optionsHydrationAttempts.add(attemptKey);
    const requestId = ++this.optionsRequestId;
    this.setState({ optionsLoading: true, optionsError: "" });
    let response = null;
    try {
      response = await this.props.fetchContextOptions(this.props.countryCode);
    } catch (error) {
      response = null;
    }
    if (
      !this.mounted ||
      !this.props.open ||
      requestId !== this.optionsRequestId
    )
      return response;

    const responseMissing = response
      ? getMissingContextOptionGroups(
          {
            ...this.props,
            countries: response.countries || [],
            areas: response.areas || [],
            branches: response.branches || [],
            branchDeliveryCharges: response.branch_delivery_charges || [],
          },
          this.state.draft,
        )
      : missing;
    this.setState({
      optionsLoading: false,
      optionsError: responseMissing.length
        ? "context_options_unavailable"
        : "",
    });
    return response;
  };

  retryContextOptions = () => {
    this.optionsHydrationAttempts.delete(this.getOptionsHydrationKey());
    return this.ensureContextOptions({ force: true });
  };

  applyAutomaticDefaults = () => {
    const changes = getAutomaticContextChanges(this.props, this.state.draft);
    if (!changes) return false;
    this.patchLocation(changes);
    return true;
  };

  maybeLoadSlots = () => {
    if (
      !this.mounted ||
      !this.props.open ||
      this.state.optionsLoading ||
      this.state.slotsLoading ||
      this.state.slots ||
      ["delivery_time_unavailable", "delivery_times_load_failed"].includes(this.state.error)
    )
      return false;
    const locationIssues = getFulfillmentIssues(this.state.draft).filter(
      (issue) =>
        [
          "fulfillment_type",
          "country",
          "area",
          "branch",
          "branch_delivery_charge",
        ].includes(issue),
    );
    if (locationIssues.length) return false;
    this.loadSlots();
    return true;
  };

  getDraftKey = (draft = this.state.draft) =>
    createFulfillmentFingerprint({ ...draft, version: draft?.version || 1 });

  patchDraft = (changes) => {
    this.previewRequestId += 1;
    this.setState((state) => ({
      draft: { ...state.draft, ...changes },
      preview: null,
      previewKey: null,
      loading: false,
      error: "",
    }));
  };

  patchTime = (changes) => {
    this.previewRequestId += 1;
    this.setState((state) => ({
      draft: {
        ...state.draft,
        time: { ...(state.draft.time || {}), ...changes },
      },
      preview: null,
      previewKey: null,
      loading: false,
      error: "",
    }));
  };

  patchVehicle = (changes) => {
    this.previewRequestId += 1;
    this.setState((state) => ({
      draft: {
        ...state.draft,
        vehicle: { ...(state.draft.vehicle || {}), ...changes },
      },
      preview: null,
      previewKey: null,
      loading: false,
      error: "",
    }));
  };

  patchLocation = (changes, { capturedPosition = null } = {}) => {
    this.previewRequestId += 1;
    this.slotsRequestId += 1;
    this.setState((state) => {
      const nextDraft = {
        ...state.draft,
        ...changes,
        time: { type: null, date: "", start: "", end: "" },
      };
      const locationDraft = invalidateAddressForLocationChange(
        state.draft,
        nextDraft,
      );
      const lat = finiteCoordinate(capturedPosition?.lat);
      const lng = finiteCoordinate(capturedPosition?.lng);
      const hasCapturedPosition =
        lat !== null &&
        lng !== null &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180;
      return {
        draft: hasCapturedPosition
          ? {
              ...locationDraft,
              address: {
                ...(locationDraft.address || {}),
                lat,
                lng,
                gpsConfirmed: true,
                capturedFromDevice: true,
              },
            }
          : locationDraft,
        preview: null,
        previewKey: null,
        slots: null,
        showSlots: false,
        selectedSlotDate: "",
        branchDetailsOpen: false,
        slotsLoading: false,
        loading: false,
        error: "",
      };
    });
  };

  setType = (type) => {
    const selectedCountry = this.props.countries.find((country) =>
      matches(country.id, this.state.draft.countryId),
    );
    this.patchLocation({
      type,
      isPickup: type === "pickup",
      pickupMode:
        type === "pickup"
          ? this.state.draft.pickupMode || "inside"
          : null,
      countryId:
        type === "delivery"
          ? selectedCountry?.id ?? this.props.countries[0]?.id ?? null
          : null,
      areaId: type === "delivery" ? this.state.draft.areaId : null,
      branchId:
        type === "delivery" ? null : getEligibleContextBranches({ branches: this.props.branches, type: "pickup" })
          .find((branch) => matches(branch.id, this.state.draft.branchId))?.id || null,
      branchDeliveryChargeId:
        type === "delivery" ? this.state.draft.branchDeliveryChargeId : null,
    });
  };

  selectCountry = (countryId) =>
    this.patchLocation({
      countryId,
      areaId: null,
      branchId: null,
      branchDeliveryChargeId: null,
    });

  selectBranch = (branchId) => {
    const nextBranch = this.props.branches.find((branch) =>
      matches(branch.id, branchId),
    );
    const nextCarConfig = nextBranch || this.props.settings;
    this.patchLocation({
      branchId,
      pickupMode:
        this.state.draft.type === "pickup" &&
        nextCarConfig.ask_for_car_info !== true
          ? "inside"
          : this.state.draft.pickupMode || "inside",
      branchDeliveryChargeId:
        this.state.draft.type === "delivery"
          ? this.resolveDeliveryCharge(branchId, this.state.draft.areaId)
          : null,
    });
  };

  selectArea = (areaId, { capturedPosition = null } = {}) => {
    if (!areaId) {
      this.patchLocation({
        areaId: null,
        branchId: null,
        branchDeliveryChargeId: null,
      });
      return;
    }
    const eligibleBranches = getEligibleContextBranches({
      branches: this.props.branches,
      branchDeliveryCharges: this.props.branchDeliveryCharges,
      type: "delivery",
      areaId,
      isBranchBased: this.props.settings.is_branch_based_ordering,
    });
    const selectedBranch = eligibleBranches[0];
    this.rememberArea(areaId);
    this.patchLocation(
      {
        areaId,
        branchId: selectedBranch?.id || null,
        branchDeliveryChargeId: selectedBranch
          ? this.resolveDeliveryCharge(selectedBranch.id, areaId)
          : null,
      },
      { capturedPosition },
    );
  };

  loadSlots = async ({ replaceCurrentAsap = false } = {}) => {
    // React batches state updates: the open callback and componentDidUpdate
    // can both reach here before slotsLoading commits. Track the network
    // request synchronously; a new location/open generation may still fetch.
    if (this.state.slotsLoading || this.slotsInFlightRequestId === this.slotsRequestId) return null;
    const draft = this.state.draft;
    const draftKey = this.getDraftKey(draft);
    const requestId = ++this.slotsRequestId;
    const issues = getFulfillmentIssues(draft).filter((issue) =>
      [
        "fulfillment_type",
        "country",
        "area",
        "branch",
        "branch_delivery_charge",
      ].includes(issue),
    );
    if (issues.length) {
      this.setState({ error: issues[0] });
      return null;
    }
    const regular = this.props.cart.cart || [];
    const promoItems = this.props.cart.promo_items || [];
    this.slotsInFlightRequestId = requestId;
    const releaseRequest = () => {
      if (this.slotsInFlightRequestId === requestId)
        this.slotsInFlightRequestId = null;
    };
    this.setState({
      slotsLoading: true,
      error: "",
      preview: null,
      previewKey: null,
    });
    try {
      const slots = await this.props.api.fetchDeliverySlots({
        prep_time: calculate_prep_time(
          [...regular, ...promoItems],
          this.props.settings.sum_product_prep_time,
        ),
        is_pickup: draft.type === "pickup",
        branch: draft.branchId,
        bdc: draft.branchDeliveryChargeId,
        pickupBranchStoreBased:
          draft.type === "pickup" &&
          !this.props.settings.is_branch_based_ordering
            ? draft.branchId
            : null,
        country: draft.countryId,
        area: draft.areaId,
        cart: this.props.cart,
        checkout_v2: true,
      });
      releaseRequest();
      if (
        !this.mounted ||
        requestId !== this.slotsRequestId ||
        draftKey !== this.getDraftKey()
      ) {
        // A time/vehicle edit can change the canonical draft without starting
        // another slot request. Release the loading state while still keeping
        // a newer request (different id) in sole control of its own spinner.
        if (this.mounted && requestId === this.slotsRequestId)
          this.setState({ slotsLoading: false });
        return null;
      }
      const scheduled = slots.pickup || slots.scheduled || {};
      if (!slots.asap && !Object.keys(scheduled).length) {
        this.setState({
          slotsLoading: false,
          slots,
          showSlots: true,
          error: slots.booking_error || "delivery_time_unavailable",
        });
        return null;
      }
      const recommended = getRecommendedContextTime(slots);
      const slotDates = Object.keys(scheduled).sort();
      const replaceAsap =
        replaceCurrentAsap &&
        this.state.draft.time?.type === "asap" &&
        recommended?.type === "asap";
      if (replaceAsap) this.previewRequestId += 1;
      await new Promise((resolve) =>
        this.setState({
          slotsLoading: false,
          slots,
          showSlots: this.state.draft.time?.type === "scheduled" || !recommended,
          selectedSlotDate:
            slotDates.includes(this.state.draft.time?.date) && this.state.draft.time?.type === "scheduled"
              ? this.state.draft.time.date
              : recommended?.type === "scheduled"
              ? recommended.date
              : slotDates[0] || "",
          error: recommended ? "" : "delivery_time_unavailable",
          ...(replaceAsap
            ? {
                draft: {
                  ...this.state.draft,
                  time: {
                    type: "asap",
                    date: recommended.value.date || "",
                    start: recommended.value.start || "",
                    end: recommended.value.end || "",
                    withinMinutes: getAsapWithinMinutes(recommended.value),
                    authority: recommended.value.authority || "",
                    authorityExpiresAt: Number(
                      recommended.value.authority_expires_at || 0,
                    ),
                  },
                },
                preview: null,
                previewKey: null,
              }
            : {}),
        }, resolve),
      );
      if (!replaceAsap && recommended && (!this.state.draft.time?.type || slots.booking_time)) {
        if (recommended.type === "asap")
          this.selectAsap(recommended.value);
        else this.selectScheduled(recommended.date, recommended.value);
      }
      return recommended;
    } catch (error) {
      releaseRequest();
      if (
        !this.mounted ||
        requestId !== this.slotsRequestId ||
        draftKey !== this.getDraftKey()
      ) {
        if (this.mounted && requestId === this.slotsRequestId)
          this.setState({ slotsLoading: false });
        return null;
      }
      this.setState({
        slotsLoading: false,
        slots: null,
        error: "delivery_times_load_failed",
      });
      return null;
    }
  };

  selectAsap = (asap) => {
    this.patchTime({
      type: "asap",
      date: asap.date || "",
      start: asap.start || "",
      end: asap.end || "",
      withinMinutes: getAsapWithinMinutes(asap),
      authority: asap.authority || "",
      authorityExpiresAt: Number(asap.authority_expires_at || 0),
    });
    this.setState({ showSlots: false });
  };

  selectScheduled = (date, slot) => {
    this.patchTime({
      type: "scheduled",
      date,
      start: slot.start,
      end: slot.end,
      authority: "",
      authorityExpiresAt: 0,
      withinMinutes: null,
    });
    this.setState({ showSlots: true, selectedSlotDate: date });
  };

  resolveDeliveryCharge = (branchId, areaId) => {
    return findDeliveryChargeId(
      this.props.branchDeliveryCharges,
      branchId,
      areaId,
    );
  };

  toggleOtherTimes = () => {
    if (!this.state.slots) {
      this.loadSlots();
      return;
    }
    const showSlots = !this.state.showSlots;
    this.setState({ showSlots }, () => {
      if (!showSlots) return;
      const scroll = () =>
        this.slotsRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "nearest",
        });
      if (typeof window.requestAnimationFrame === "function")
        window.requestAnimationFrame(scroll);
      else scroll();
    });
  };

  selectSlotDate = (date) => {
    if (this.state.draft.time?.type !== "scheduled" || this.state.draft.time?.date !== date)
      this.patchTime({ type: "scheduled", date, start: "", end: "", withinMinutes: null, authority: "", authorityExpiresAt: 0 });
    this.setState({ showSlots: true, selectedSlotDate: date }, () => {
      const selectedDay = this.slotDaysRef.current?.querySelector('[aria-pressed="true"]');
      if (typeof selectedDay?.scrollIntoView === "function")
        selectedDay.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
    });
  };

  showScheduledTimes = async () => {
    if (!this.state.slots) {
      await this.loadSlots();
      if (!this.mounted || !this.props.open || !this.state.slots) return;
    }
    const dates = Object.keys(this.state.slots.pickup || this.state.slots.scheduled || {}).sort();
    if (!dates.length) return;
    const date = this.state.draft.time?.type === "scheduled" && dates.includes(this.state.draft.time.date)
      ? this.state.draft.time.date
      : dates.includes(this.state.selectedSlotDate) ? this.state.selectedSlotDate : dates[0];
    this.selectSlotDate(date);
    this.setState({ showSlots: true }, () => {
      const scroll = () =>
        this.slotsRef.current?.scrollIntoView?.({
          behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "nearest",
        });
      if (typeof window.requestAnimationFrame === "function")
        window.requestAnimationFrame(scroll);
      else scroll();
    });
  };

  preview = async ({
    applyOnSuccess = false,
    confirmedConsequences = false,
  } = {}) => {
    const draft = {
      ...this.state.draft,
      fingerprint: this.getDraftKey(this.state.draft),
    };
    const draftKey = this.getDraftKey(draft);
    const requestId = ++this.previewRequestId;
    const issues = getFulfillmentIssues(draft, { requireTime: true }).filter(
      (issue) =>
      [
        "fulfillment_type",
        "country",
        "area",
        "branch",
        "branch_delivery_charge",
        "fulfillment_time",
      ].includes(issue),
    );
    if (issues.length) {
      if (issues[0] === "fulfillment_time") this.loadSlots();
      this.setState({ error: issues[0] });
      return false;
    }
    this.setState({ loading: true, error: "" });
    try {
      const preview = await this.props.api.validateCartContext({
        context: draft,
        lines: buildCheckoutLines(this.props.cart),
        draft: this.props.checkoutDraft,
        currencyCode: this.props.countryCode || null,
      });
      if (
        !this.mounted ||
        requestId !== this.previewRequestId ||
        draftKey !== this.getDraftKey()
      )
        return false;
      const valid = preview.success === true && preview.valid === true;
      await new Promise((resolve) =>
        this.setState(
          {
            loading: false,
            preview,
            previewKey: draftKey,
            error: valid ? "" : preview.errors[0] || "context_invalid",
          },
          resolve,
        ),
      );
      const hasConsequences =
        (preview.removedLines || []).length > 0 ||
        (preview.adjustedLines || []).length > 0;
      if (
        valid &&
        applyOnSuccess &&
        (confirmedConsequences || !hasConsequences)
      )
        this.commitPreview();
      return valid;
    } catch (error) {
      if (
        !this.mounted ||
        requestId !== this.previewRequestId ||
        draftKey !== this.getDraftKey()
      )
        return false;
      this.setState({ loading: false, error: "context_unavailable" });
      return false;
    }
  };

  refreshExpiredAsapAndApply = async ({
    confirmedConsequences = false,
  } = {}) => {
    this.setState({ loading: true, error: "" });
    const recommended = await this.loadSlots({ replaceCurrentAsap: true });
    if (!recommended || recommended.type !== "asap") {
      this.setState({
        loading: false,
        error: "delivery_time_unavailable",
      });
      return false;
    }
    return this.preview({ applyOnSuccess: true, confirmedConsequences });
  };

  reviewSelection = () => {
    if (
      this.state.draft.time?.type === "asap" &&
      (!this.state.draft.time.authority ||
        !this.state.draft.time.authorityExpiresAt ||
        this.state.draft.time.authorityExpiresAt <= Date.now())
    )
      return this.refreshExpiredAsapAndApply();
    return this.preview({ applyOnSuccess: true });
  };

  commitPreview = () => {
    if (
      !this.state.preview?.success ||
      !this.state.preview?.valid ||
      this.state.previewKey !== this.getDraftKey()
    )
      return false;
    const context = {
      ...this.state.draft,
      fingerprint: this.getDraftKey(this.state.draft),
    };
    batch(() => {
      this.props.commitContext(context);
      this.props.setValidation({
        ...this.state.preview,
        contextFingerprint: context.fingerprint,
        cartFingerprint: createCartFingerprint(buildCheckoutLines(this.props.cart)),
      });
    });
    if ((this.state.preview.removedLines || []).length)
      (this.props.telemetry || recordStorefrontV2Telemetry)(
        STOREFRONT_V2_TELEMETRY.CART_LINE_REMOVED,
        "context_change",
      );
    if (this.props.onApplied) this.props.onApplied(context);
    this.props.onClose();
    return true;
  };

  apply = () => {
    if (
      !this.state.preview?.success ||
      !this.state.preview?.valid ||
      this.state.previewKey !== this.getDraftKey()
    )
      return;
    if (
      this.state.draft.time?.type === "asap" &&
      (!this.state.draft.time.authority ||
        !this.state.draft.time.authorityExpiresAt ||
        this.state.draft.time.authorityExpiresAt <= Date.now())
    ) {
      this.setState({
        preview: null,
        previewKey: null,
        error: "",
      });
      return this.refreshExpiredAsapAndApply({
        confirmedConsequences: true,
      });
    }
    return this.commitPreview();
  };

  renderSelect(label, value, options, onChange, configuration = {}) {
    const arabic = this.props.language === "arabic";
    const {
      disabled = false,
      help = "",
      placeholder = arabic ? "اختر" : "Choose",
    } = configuration;
    return (
      <label className="context-sheet__field">
        <span className="context-sheet__field-label">{label}</span>
        <select
          aria-label={label}
          className="context-sheet__select"
          disabled={disabled}
          value={value === null || value === undefined ? "" : value}
          onChange={(event) => onChange(id(event.target.value))}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {optionLabel(option, arabic)}
            </option>
          ))}
        </select>
        {help && <span className="context-sheet__field-help">{help}</span>}
      </label>
    );
  }

  renderSearchSelect(label, value, options, onChange, configuration = {}) {
    const arabic = this.props.language === "arabic";
    const {
      disabled = false,
      help = "",
      placeholder = arabic ? "ابحث عن منطقتك" : "Search for your area",
      noOptionsText = arabic ? "لا توجد نتيجة مطابقة" : "No matching option",
      groupBy,
      renderOption,
      hideLabel = false,
    } = configuration;
    const selected =
      options.find((option) => matches(option.id, value)) || null;
    return (
      <div className="context-sheet__field context-sheet__field--search">
        {!hideLabel && <span className="context-sheet__field-label">{label}</span>}
        <Autocomplete
          autoHighlight
          disablePortal={Boolean(this.state.mobileViewport)}
          className="context-sheet__autocomplete"
          classes={{ listbox: "storefront-v2-surface context-sheet__search-results", popper: "context-sheet__search-popper" }}
          ListboxProps={{ dir: arabic ? "rtl" : "ltr", onScroll: event => {
            const list = event.currentTarget;
            if (list.scrollHeight - list.scrollTop - list.clientHeight < 160)
              this.loadMoreSearchOptions();
          } }}
          disabled={disabled}
          filterOptions={(availableOptions, filterState) => {
            this.searchResults = filterContextSelectOptions(
              availableOptions,
              filterState,
              selected,
              arabic,
            );
            // Filter the complete dataset before paging: search must find areas
            // beyond the currently rendered rows without mounting thousands of options.
            return this.searchResults.slice(0, this.state.searchOptionLimit);
          }}
          getOptionLabel={(option) => optionLabel(option, arabic)}
          getOptionSelected={(option, selectedOption) =>
            matches(option.id, selectedOption.id)
          }
          groupBy={groupBy}
          noOptionsText={noOptionsText}
          inputValue={this.state.searchKey === label ? this.state.searchText : selected ? optionLabel(selected, arabic) : ""}
          onInputChange={(event, text, reason) => {
            // Options are reconstructed when the viewport/slots update. MUI's
            // automatic "reset" must not overwrite a query being typed.
            if (reason === "input" || reason === "clear")
              this.setState({ searchKey: label, searchText: text, searchOptionLimit: CONTEXT_OPTION_PAGE_SIZE });
          }}
          onOpen={this.openSearch}
          onClose={this.closeSearch}
          blurOnSelect="touch"
          onChange={(event, option) => onChange(option ? id(option.id) : null)}
          options={options}
          renderOption={renderOption}
          selectOnFocus
          value={selected}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              inputProps={{
                ...params.inputProps,
                "aria-label": label,
                onKeyUp: event => {
                  if (params.inputProps.onKeyUp) params.inputProps.onKeyUp(event);
                  if (!["ArrowDown", "PageDown", "End"].includes(event.key)) return;
                  const activeId = event.currentTarget.getAttribute("aria-activedescendant");
                  const activeIndex = document.getElementById(activeId)?.getAttribute("data-option-index");
                  if (activeIndex !== null && Number(activeIndex) >= this.state.searchOptionLimit - 1)
                    this.loadMoreSearchOptions();
                },
              }}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
        />
        {help && <span className="context-sheet__field-help">{help}</span>}
      </div>
    );
  }

  render() {
    const {
      open,
      onClose,
      language,
      settings,
      countries,
      areas,
      branches,
      branchDeliveryCharges,
      dismissible = true,
    } = this.props;
    const arabic = language === "arabic";
    const { draft, preview } = this.state;
    const contextAreas = deriveContextAreas(areas, branchDeliveryCharges);
    const filteredAreas = contextAreas.filter((area) => {
      const countryId = area.country || area.country_id;
      return (
        !draft.countryId || !countryId || matches(countryId, draft.countryId)
      );
    });
    const modeAvailability = getContextModeAvailability(settings, branches);
    const canDelivery = modeAvailability.delivery;
    const canPickup = modeAvailability.pickup;
    const eligibleBranches = getEligibleContextBranches({
      branches,
      branchDeliveryCharges,
      type: draft.type,
      areaId: draft.areaId,
      isBranchBased: settings.is_branch_based_ordering,
    });
    const selectedBranch = branches.find((branch) =>
      matches(branch.id, draft.branchId),
    );
    const carConfig = selectedBranch || settings;
    const timePresentation = { settings, branch: selectedBranch, type: draft.type, cart: this.props.cart };
    const canCarPickup = carConfig.ask_for_car_info === true;

    const fulfillmentIssues = getFulfillmentIssues(draft, {
      requireTime: true,
    }).filter((issue) =>
      [
        "fulfillment_type",
        "country",
        "area",
        "branch",
        "branch_delivery_charge",
        "fulfillment_time",
      ].includes(issue),
    );
    const locationIssues = fulfillmentIssues.filter(
      (issue) => issue !== "fulfillment_time",
    );
    let canReview =
      hasSelectedFulfillmentMode(draft) && fulfillmentIssues.length === 0;
    const previewIsCurrent =
      preview?.success &&
      preview?.valid &&
      this.state.previewKey === this.getDraftKey();
    const removedCount = preview?.removedLines?.length || 0;
    const adjustedCount = preview?.adjustedLines?.length || 0;
    const previewHasChanges = removedCount > 0 || adjustedCount > 0;
    const previewNeedsConfirmation = previewIsCurrent && previewHasChanges;
    const ContinueIcon = arabic ? ArrowBackIcon : ArrowForwardIcon;
    const selectedArea = contextAreas.find((area) =>
      matches(area.id, draft.areaId),
    );
    const selectedLocation =
      draft.type === "pickup"
        ? selectedBranch && optionLabel(selectedBranch, arabic)
        : draft.type === "delivery"
          ? selectedArea && optionLabel(selectedArea, arabic)
          : "";
    const selectedAreaCommerce = getSelectedContextAreaCommerce(
      selectedArea,
      draft.branchDeliveryChargeId,
      branchDeliveryCharges,
      { settings, countries, baseCountryId: this.props.baseCountryId },
    );
    const scheduledSlotDays =
      this.state.slots?.pickup || this.state.slots?.scheduled || {};
    const slotDates = Object.keys(scheduledSlotDays).sort();
    const selectedSlotDate = slotDates.includes(this.state.selectedSlotDate)
      ? this.state.selectedSlotDate
      : slotDates[0] || "";
    const selectedSlotDay = scheduledSlotDays[selectedSlotDate] || null;
    const recentAreas = this.state.recentAreaIds
      .map((areaId) => filteredAreas.find((area) => matches(area.id, areaId)))
      .filter(Boolean);
    const areaGroups = groupContextAreas(filteredAreas, language);
    const groupedAreaOptions = areaGroups.reduce(
      (options, group) => options.concat(group.options),
      [],
    );
    const areaGroupById = areaGroups.reduce((lookup, group) => {
      group.options.forEach((area) => {
        lookup[`${area.id}`] = group.label;
      });
      return lookup;
    }, {});
    const branchDistances = eligibleBranches.reduce((lookup, branch) => {
      lookup[`${branch.id}`] = this.state.userPosition
        ? getContextDistanceKm(this.state.userPosition, branch)
        : null;
      return lookup;
    }, {});
    const needsScheduledTime = draft.time?.type === "scheduled" && (!draft.time.start || !draft.time.end);
    const currentTimeLabel = needsScheduledTime
      ? arabic ? "اختر وقتاً" : "Choose a time"
      : draft.time?.type === "asap"
        ? getContextAsapLabel(draft.time, language, undefined, timePresentation)
        : draft.time?.type === "scheduled"
          ? formatCheckoutTimeSummary(draft.time, arabic)
          : "";
    const recap = [
      draft.type === "pickup"
        ? arabic
          ? "استلام"
          : "Pickup"
        : draft.type === "delivery"
          ? arabic
            ? "توصيل"
            : "Delivery"
          : "",
      selectedLocation,
      currentTimeLabel,
    ]
      .filter(Boolean)
      .join(" · ");
    const selectedAreaServed =
      draft.type !== "delivery" ||
      !draft.areaId ||
      (selectedArea?.served !== false &&
        (!settings.is_branch_based_ordering || eligibleBranches.length > 0));
    canReview = canReview && selectedAreaServed;
    const nearestServedArea = !selectedAreaServed
      ? getNearestServedContextArea(
          selectedArea,
          filteredAreas,
          branchDeliveryCharges,
        )
      : null;
    const hasScheduledTimes = slotDates.length > 0;
    const hasAsapTime = Boolean(this.state.slots?.asap) && this.state.slots.asap.eligible !== false;
    const showTimeEstimate = !hasScheduledTimes && hasAsapTime && !locationIssues.length;
    if (showTimeEstimate && draft.time?.type === "scheduled") canReview = false;
    const timeChoice = this.state.showSlots ? "scheduled" : draft.time?.type;
    const actionLabel = needsScheduledTime
      ? arabic ? "اختر وقتاً" : "Choose a time"
      : hasSelectedFulfillmentMode(this.props.context)
        ? arabic ? "حفظ التغييرات" : "Save changes"
        : arabic ? "تأكيد الخيارات" : "Confirm options";
    const selectedDateIndex = slotDates.indexOf(selectedSlotDate);

    return (
      <Drawer
        style={{ zIndex: 1600 }}
        anchor={arabic ? "left" : "right"}
        open={open}
        onClose={dismissible ? onClose : () => {}}
        PaperProps={{
          className: "checkout-v2-context-paper",
          style: this.state.mobileViewport ? {
            top: this.state.mobileViewport.top,
            height: this.state.mobileViewport.height,
          } : undefined,
        }}
      >
        <div
          className={`context-sheet storefront-v2-surface${this.state.searchOpen ? " is-searching" : ""}`}
          dir={arabic ? "rtl" : "ltr"}
          role="dialog"
          aria-label={arabic ? "تغيير خيارات الطلب" : "Change order options"}
          style={{
            "--context-accent": settings.dark || "#0099cc",
            direction: arabic ? "rtl" : "ltr",
          }}
        >
          <header className="context-sheet__header">
            <div className="context-sheet__title">
              <svg className="context-sheet__route-art" viewBox="0 0 52 40" aria-hidden="true" focusable="false">
                <rect x="0" y="0" width="52" height="40" rx="12" fill="currentColor" opacity=".08" />
                <path d="M11 29h22a7 7 0 0 0 7-7v-1" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 4" strokeLinecap="round" opacity=".55" />
                <circle cx="11" cy="29" r="3" fill="currentColor" />
                <path d="M40 7a7 7 0 0 0-7 7c0 5 7 10 7 10s7-5 7-10a7 7 0 0 0-7-7Z" fill="white" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                <circle cx="40" cy="14" r="2.2" fill="currentColor" />
              </svg>
              <h2>{arabic ? "خيارات الطلب" : "Order options"}</h2>
            </div>
            {dismissible && (
              <button
                aria-label={arabic ? "إغلاق" : "Close"}
                className="context-sheet__close"
                onClick={onClose}
                type="button"
              >
                <CloseIcon />
              </button>
            )}
          </header>

          <main className="context-sheet__body" ref={this.bodyRef} onScroll={this.handleBodyScroll}>
            <DeferredFeedback active={this.state.optionsLoading}>
              <div className="context-sheet__notice" role="status">
                <CircularProgress size={18} />
                <span>
                  {arabic
                    ? "جاري تجهيز المواقع المتاحة…"
                    : "Getting available locations…"}
                </span>
              </div>
            </DeferredFeedback>
            {this.state.optionsError && (
              <div
                className="context-sheet__notice context-sheet__notice--error"
                role="alert"
              >
                <span>
                  {getContextErrorMessage(this.state.optionsError, arabic)}
                </span>
                <button
                  className="context-sheet__notice-action"
                  disabled={this.state.optionsLoading}
                  onClick={this.retryContextOptions}
                  type="button"
                >
                  {arabic ? "إعادة المحاولة" : "Try again"}
                </button>
              </div>
            )}

            <section
              className={`context-sheet__section context-sheet__section--mode ${
                hasSelectedFulfillmentMode(draft) ? "is-compact" : ""
              } ${this.state.modeCollapsed ? "is-scrolled" : ""}`}
            >
              <div className="context-sheet__section-heading">
                <div>
                  <h3>{arabic ? "اختر الطريقة" : "Choose a method"}</h3>
                </div>
              </div>
              <div className="context-sheet__mode-grid">
                {canDelivery && (
                  <button
                    aria-pressed={draft.type === "delivery"}
                    className={`context-sheet__mode ${
                      draft.type === "delivery" ? "is-selected" : ""
                    }`}
                    onClick={() => this.setType("delivery")}
                    type="button"
                  >
                    <span className="context-sheet__mode-icon">
                      <LocalShippingOutlinedIcon />
                    </span>
                    <span className="context-sheet__mode-label">
                      <strong>{arabic ? "توصيل" : "Delivery"}</strong>
                      <small>{arabic ? "إلى عنوانك" : "To your address"}</small>
                    </span>
                  </button>
                )}
                {canPickup && (
                  <button
                    aria-pressed={draft.type === "pickup"}
                    className={`context-sheet__mode ${
                      draft.type === "pickup" ? "is-selected" : ""
                    }`}
                    onClick={() => this.setType("pickup")}
                    type="button"
                  >
                    <span className="context-sheet__mode-icon">
                      <StorefrontOutlinedIcon />
                    </span>
                    <span className="context-sheet__mode-label">
                      <strong>{arabic ? "استلام" : "Pickup"}</strong>
                      <small>{arabic ? "من الفرع" : "From a branch"}</small>
                    </span>
                  </button>
                )}
              </div>
            </section>

            {hasSelectedFulfillmentMode(draft) && (
              <section className="context-sheet__section">
                <div className="context-sheet__section-heading context-sheet__section-heading--illustrated">
                  <span className="context-sheet__section-icon" aria-hidden="true">
                    {draft.type === "delivery" ? <RoomOutlinedIcon /> : <StorefrontOutlinedIcon />}
                  </span>
                  <div>
                    <h3>
                      {draft.type === "delivery"
                        ? arabic
                          ? "منطقة التوصيل"
                          : "Delivery area"
                        : arabic
                          ? "اختر فرع الاستلام"
                          : "Choose a pickup branch"}
                    </h3>
                  </div>
                </div>

                <div className="context-sheet__fields">
                  {draft.type === "delivery" && countries.length > 1 &&
                    <ContextCountryChoices countries={countries} selectedId={draft.countryId}
                      onSelect={this.selectCountry} language={this.props.language} getLabel={optionLabel} />}
                  {draft.type === "delivery" &&
                    <>
                      {this.renderSearchSelect(
                      arabic ? "المنطقة" : "Area",
                      draft.areaId,
                      groupedAreaOptions,
                      this.selectArea,
                      {
                        hideLabel: countries.length <= 1,
                        disabled: !draft.countryId,
                        help: !draft.countryId
                          ? arabic
                            ? "اختر الدولة أولاً"
                            : "Choose a country first"
                          : "",
                        placeholder: arabic
                          ? "ابحث عن منطقتك"
                          : "Search for your area",
                        noOptionsText: arabic
                          ? "لا توجد منطقة مطابقة"
                          : "No matching area",
                        groupBy: (area) => areaGroupById[`${area.id}`] || "",
                        renderOption: (area) => {
                          const commerce = getContextAreaCommerce(
                            area.id,
                            branchDeliveryCharges,
                            contextAreas,
                            { settings, countries, baseCountryId: this.props.baseCountryId },
                          );
                          return (
                            <div className="context-sheet__area-option">
                              <span>{optionLabel(area, arabic)}</span>
                              {(commerce.feePending || commerce.fee !== null || commerce.minimum !== null) && (
                                <small>
                                  {commerce.feePending && (arabic ? "الرسوم تحدد في السلة" : "Fee calculated in cart")}
                                  {commerce.fee !== null && (
                                    <>
                                      {arabic ? "رسوم " : "Fee "}
                                      <Money
                                        currency={settings.currency_iso}
                                        decimals={settings.currency_decimals}
                                        language={language}
                                        value={commerce.fee}
                                      />
                                    </>
                                  )}
                                  {(commerce.feePending || commerce.fee !== null) && commerce.minimum !== null ? " · " : ""}
                                  {commerce.minimum !== null && (
                                    <>
                                      {arabic ? "حد أدنى " : "min "}
                                      <Money
                                        currency={settings.currency_iso}
                                        decimals={settings.currency_decimals}
                                        language={language}
                                        value={commerce.minimum}
                                      />
                                    </>
                                  )}
                                  {commerce.varies ? (arabic ? " فأكثر" : "+") : ""}
                                </small>
                              )}
                            </div>
                          );
                        },
                      },
                    )}
                    {draft.areaId &&
                      (selectedAreaCommerce.feePending || selectedAreaCommerce.fee !== null ||
                        selectedAreaCommerce.minimum !== null) && (
                        <div className="context-sheet__area-commerce" role="status">
                          {selectedAreaCommerce.feePending && (
                            <div>
                              <span>{arabic ? "رسوم التوصيل" : "Delivery fee"}</span>
                              <strong>{arabic ? "تحدد في السلة" : "TBD · calculated in cart"}</strong>
                            </div>
                          )}
                          {selectedAreaCommerce.fee !== null && (
                            <div>
                              <span>{arabic ? "رسوم التوصيل" : "Delivery fee"}</span>
                              <strong>
                                <Money
                                  currency={settings.currency_iso}
                                  decimals={settings.currency_decimals}
                                  language={language}
                                  value={selectedAreaCommerce.fee}
                                />
                              </strong>
                            </div>
                          )}
                          {selectedAreaCommerce.minimum !== null && (
                            <div>
                              <span>{arabic ? "الحد الأدنى للطلب" : "Minimum order"}</span>
                              <strong>
                                {moneyToMajor(selectedAreaCommerce.minimum) === 0
                                  ? arabic ? "لا يوجد حد أدنى" : "No minimum order"
                                  : <Money
                                  currency={settings.currency_iso}
                                  decimals={settings.currency_decimals}
                                  language={language}
                                  value={selectedAreaCommerce.minimum}
                                />}
                              </strong>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="context-sheet__area-tools">
                        <button className="context-sheet__location-action" disabled={this.state.locating || !draft.countryId} onClick={this.useMyLocation} type="button">
                          {this.state.locating ? <CircularProgress size={17} /> : <MyLocationOutlinedIcon />}
                          {this.state.locating ? arabic ? "جاري تحديد موقعك…" : "Finding your location…" : arabic ? "استخدم موقعي" : "Use my location"}
                        </button>
                        {!!recentAreas.length && <div className="context-sheet__recent-areas">
                          <span>{arabic ? "مؤخراً" : "Recent"}</span>
                          {recentAreas.map((area) => <button className={matches(area.id, draft.areaId) ? "is-selected" : ""} key={area.id} onClick={() => this.selectArea(area.id)} type="button">{optionLabel(area, arabic)}</button>)}
                        </div>}
                      </div>
                      {this.state.locationError && <p className="context-sheet__inline-help" role="status">
                        {this.state.locationError === "location_denied"
                          ? arabic ? "تعذر الوصول إلى موقعك. ابحث عن منطقتك في الأعلى." : "Location permission is blocked. Search for your area above."
                          : arabic ? "تعذر تحديد منطقتك الآن. حاول مجدداً أو اخترها في الأعلى." : "We couldn’t find your area right now. Try again or choose it above."}
                      </p>}
                    </>}
                  {draft.type === "pickup" && (
                    <div className="context-sheet__area-tools">
                      <button
                        className="context-sheet__location-action"
                        disabled={this.state.locating}
                        onClick={this.useMyLocation}
                        type="button"
                      >
                        {this.state.locating ? (
                          <CircularProgress size={17} />
                        ) : (
                          <MyLocationOutlinedIcon />
                        )}
                        {this.state.locating
                          ? arabic
                            ? "جاري ترتيب الفروع…"
                            : "Finding nearby branches…"
                          : arabic
                            ? "إظهار الأقرب إليّ"
                            : "Show nearest to me"}
                      </button>
                      {this.state.locationError ===
                        "location_branch_unavailable" && (
                        <p className="context-sheet__inline-help" role="status">
                          {arabic
                            ? "تعذر حساب المسافة. لا يزال بإمكانك اختيار الفرع أدناه."
                            : "We couldn’t calculate distance. You can still choose a branch below."}
                        </p>
                      )}
                    </div>
                  )}
                  {draft.type === "pickup" && (
                    <div className="context-sheet__branch-picker">
                      <div className="context-sheet__branch-list">
                        {eligibleBranches.map((branch) => {
                          const areaCharge = branchDeliveryCharges.find(
                            (charge) =>
                              matches(getChargeBranchId(charge), branch.id) &&
                              (!draft.areaId ||
                                matches(getChargeAreaId(charge), draft.areaId)),
                          );
                          const branchState = getContextBranchState(
                            { ...branch, ...areaCharge },
                            language,
                          );
                          const distance = branchDistances[`${branch.id}`];
                          const statusLabel = !branchState.open || branch.is_open === true || branch.open_now === true ? branchState.label : "";
                          return (
                            <button
                              aria-pressed={matches(branch.id, draft.branchId)}
                              className={matches(branch.id, draft.branchId) ? "is-selected" : ""}
                              key={branch.id}
                              onClick={() => this.selectBranch(branch.id)}
                              type="button"
                            >
                              <span>
                                <strong>{optionLabel(branch, arabic)}</strong>
                                {!branch.hide_branch && formatPickupBranchAddress(branch, arabic, this.props.baseCountry, true) && <small>{formatPickupBranchAddress(branch, arabic, this.props.baseCountry, true)}</small>}
                                {(statusLabel || distance !== null) && <small className={branchState.open ? "is-open" : "is-closed"}>
                                  {statusLabel}
                                  {distance !== null
                                    ? `${statusLabel ? " · " : ""}${distance < 10 ? distance.toFixed(1) : Math.round(distance)} ${arabic ? "كم" : "km"}`
                                    : ""}
                                </small>}
                              </span>
                              <CheckCircleOutlineIcon />
                            </button>
                          );
                        })}
                      </div>
                      {selectedBranch && !selectedBranch.hide_branch && (
                        <button
                          className="context-sheet__branch-details"
                          type="button"
                          aria-expanded={this.state.branchDetailsOpen}
                          aria-controls="context-branch-details"
                          onClick={() => this.setState((state) => ({ branchDetailsOpen: !state.branchDetailsOpen }))}
                        >
                          {arabic ? "تفاصيل الفرع" : "Branch details"}
                          <ExpandMoreIcon />
                        </button>
                      )}
                      {selectedBranch && !selectedBranch.hide_branch && this.state.branchDetailsOpen && <ContextBranchDetails
                        key={selectedBranch.id}
                        branch={selectedBranch}
                        language={language}
                        baseCountry={this.props.baseCountry}
                        loadDetails={this.props.loadBranchDetails}
                      />}
                    </div>
                  )}
                  {draft.type === "delivery" &&
                    draft.areaId &&
                    !selectedAreaServed && (
                      <div className="context-sheet__unserved" role="status">
                        <strong>
                          {arabic
                            ? `لا نوصل إلى ${selectedArea ? optionLabel(selectedArea, arabic) : "هذه المنطقة"} بعد.`
                            : `We don’t deliver to ${selectedArea ? optionLabel(selectedArea, arabic) : "this area"} yet.`}
                        </strong>
                        <p>
                          {nearestServedArea
                            ? arabic
                              ? `أقرب منطقة مخدومة: ${optionLabel(nearestServedArea, arabic)}`
                              : `Nearest served area: ${optionLabel(nearestServedArea, arabic)}`
                            : arabic
                              ? "يمكنك اختيار الاستلام من أحد فروعنا."
                              : "You can still collect from one of our branches."}
                        </p>
                        <div>
                          {nearestServedArea && (
                            <button
                              onClick={() => this.selectArea(nearestServedArea.id)}
                              type="button"
                            >
                              {arabic ? "اختيار أقرب منطقة" : "Choose nearest area"}
                            </button>
                          )}
                          {canPickup && (
                            <button onClick={() => this.setType("pickup")} type="button">
                              {arabic ? "التبديل إلى الاستلام" : "Switch to pickup"}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                </div>

                {draft.type === "pickup" && selectedBranch && canCarPickup && (
                  <div className="context-sheet__pickup-mode">
                    <span className="context-sheet__field-label">
                      {arabic ? "طريقة الاستلام" : "Pickup preference"}
                    </span>
                    <div className="context-sheet__mini-toggle">
                      {(canCarPickup ? ["inside", "car"] : ["inside"]).map(
                        (mode) => (
                          <button
                            aria-pressed={draft.pickupMode === mode}
                            className={
                              draft.pickupMode === mode ? "is-selected" : ""
                            }
                            key={mode}
                            onClick={() =>
                              this.patchDraft({ pickupMode: mode })
                            }
                            type="button"
                          >
                            {mode === "inside"
                              ? arabic
                                ? "الاستلام من الفرع"
                                : "Collect in store"
                              : arabic
                                ? "من السيارة"
                                : "From my car"}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {draft.type === "pickup" && selectedBranch && draft.pickupMode === "car" && (
                  <div className="context-sheet__vehicle-grid">
                    <TextField
                      className="context-sheet__vehicle-field"
                      required={carConfig.make_is_required === true}
                      label={arabic ? "نوع السيارة" : "Car make"}
                      value={draft.vehicle?.make || ""}
                      variant="outlined"
                      onChange={(event) =>
                        this.patchVehicle({ make: event.target.value })
                      }
                    />
                    <TextField
                      className="context-sheet__vehicle-field"
                      required={carConfig.color_is_required === true}
                      label={arabic ? "لون السيارة" : "Car color"}
                      value={draft.vehicle?.color || ""}
                      variant="outlined"
                      onChange={(event) =>
                        this.patchVehicle({ color: event.target.value })
                      }
                    />
                    <TextField
                      className="context-sheet__vehicle-field context-sheet__vehicle-field--wide"
                      required={carConfig.plate_is_required === true}
                      label={arabic ? "رقم اللوحة" : "Plate number"}
                      value={draft.vehicle?.plateNumber || ""}
                      variant="outlined"
                      onChange={(event) =>
                        this.patchVehicle({ plateNumber: event.target.value })
                      }
                    />
                  </div>
                )}
                {showTimeEstimate && (
                  <div className="context-sheet__time-estimate">
                    <AccessTimeOutlinedIcon aria-hidden="true" />
                    <div>
                      <span>{draft.type === "pickup" ? arabic ? "وقت الاستلام" : "Pickup time" : arabic ? "وقت التوصيل" : "Delivery time"}</span>
                      <strong><AsapTimeLabel time={this.state.slots.asap} language={language} presentation={timePresentation} /></strong>
                    </div>
                    {draft.time?.type === "scheduled" && (
                      <button type="button" onClick={() => this.selectAsap(this.state.slots.asap)}>
                        {arabic ? "استخدام أقرب وقت" : "Use ASAP"}
                      </button>
                    )}
                  </div>
                )}
              </section>
            )}

            {this.state.slots?.booking_time && hasScheduledTimes && (
              <section className="context-sheet__section">
                <h3>{arabic ? "موعد الحجز" : "Booking time"}</h3>
                <p>{formatCheckoutTimeSummary(draft.time, arabic, undefined, timePresentation)}</p>
                <p>{arabic ? "حسب المواعيد المختارة للمنتجات. لتعديل الموعد، عدّل الحجز في السلة." : "Uses your selected product booking times. To change a time, edit the booking in your cart."}</p>
              </section>
            )}
            {!this.state.slots?.booking_time && hasScheduledTimes && hasSelectedFulfillmentMode(draft) &&
              (draft.type !== "pickup" || Boolean(draft.branchId)) && (
              <section className="context-sheet__section">
                <div className="context-sheet__section-heading context-sheet__section-heading--illustrated">
                  <span className="context-sheet__section-icon" aria-hidden="true">
                    <EventOutlinedIcon />
                  </span>
                  <div>
                    <h3>{draft.type === "pickup" ? arabic ? "وقت الاستلام" : "Pickup time" : arabic ? "وقت التوصيل" : "Delivery time"}</h3>
                  </div>
                </div>

                {hasAsapTime && <div className="context-sheet__when-toggle">
                  <button
                    aria-pressed={timeChoice === "asap"}
                    className={timeChoice === "asap" ? "is-selected" : ""}
                    disabled={
                      this.state.slotsLoading ||
                      locationIssues.length > 0 ||
                      !this.state.slots?.asap
                    }
                    onClick={() => this.selectAsap(this.state.slots.asap)}
                    type="button"
                  >
                    <span>
                      {(!this.state.slots?.asap ||
                        getContextAsapLabel(this.state.slots.asap, language, undefined, timePresentation).trim().toLowerCase() !==
                          (arabic ? "في أقرب وقت" : "asap")) &&
                        <small>{arabic ? "في أقرب وقت" : "ASAP"}</small>}
                      <strong>
                        {this.state.slots?.asap
                            ? <AsapTimeLabel time={this.state.slots.asap} language={language} presentation={timePresentation} />
                            : this.state.slotsLoading ? (
                              <DeferredFeedback active fallback="—">
                                {arabic ? "جاري عرض المواعيد…" : "Loading times…"}
                              </DeferredFeedback>
                            ) : arabic
                              ? "غير متاح الآن"
                              : "Not available now"}
                      </strong>
                    </span>
                  </button>
                  <button
                    aria-pressed={timeChoice === "scheduled"}
                    className={timeChoice === "scheduled" ? "is-selected" : ""}
                    disabled={
                      this.state.slotsLoading ||
                      locationIssues.length > 0 ||
                      !hasScheduledTimes
                    }
                    onClick={this.showScheduledTimes}
                    type="button"
                  >
                    <span>
                      <strong>{arabic ? "تحديد موعد" : "Schedule"}</strong>
                    </span>
                  </button>
                </div>}
                {locationIssues.length > 0 && (
                  <p className="context-sheet__inline-help">
                    {getContextErrorMessage(locationIssues[0], arabic)}
                  </p>
                )}

                {this.state.slots && (this.state.showSlots || !hasAsapTime) && (
                  <div className="context-sheet__slots" ref={this.slotsRef}>
                    <div className="context-sheet__date-heading">
                      <strong>{arabic ? "اختر اليوم" : "Choose a day"}</strong>
                      <div>
                        <button type="button" aria-label={arabic ? "اليوم السابق" : "Previous day"} disabled={selectedDateIndex <= 0} onClick={() => this.selectSlotDate(slotDates[selectedDateIndex - 1])}>{arabic ? <ChevronRightIcon /> : <ChevronLeftIcon />}</button>
                        <button type="button" aria-label={arabic ? "اليوم التالي" : "Next day"} disabled={selectedDateIndex >= slotDates.length - 1} onClick={() => this.selectSlotDate(slotDates[selectedDateIndex + 1])}>{arabic ? <ChevronLeftIcon /> : <ChevronRightIcon />}</button>
                      </div>
                    </div>
                    {slotDates.length > 0 && (
                      <div
                        aria-label={draft.type === "pickup"
                          ? arabic ? "أيام الاستلام" : "Pickup days"
                          : arabic ? "أيام التوصيل" : "Delivery days"}
                        className="context-sheet__slot-days"
                        role="group"
                        ref={this.slotDaysRef}
                      >
                        {slotDates.map((date) => (
                          <button
                            aria-pressed={selectedSlotDate === date}
                            aria-label={formatCheckoutTimeSummary({ date }, arabic)}
                            className={
                              selectedSlotDate === date ? "is-selected" : ""
                            }
                            key={date}
                            onClick={() => this.selectSlotDate(date)}
                            type="button"
                          >
                            {formatContextSlotDate(date, language)}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedSlotDay && (
                      <div className="context-sheet__slot-day">
                        <strong className="context-sheet__slot-prompt">{arabic ? "اختر الوقت" : "Choose a time"}</strong>
                        <div className="context-sheet__slot-grid">
                          {getContextDisplaySlots(selectedSlotDay).map((slot) => {
                            const eligible = isContextSlotEligible(
                              selectedSlotDay,
                              slot,
                            );
                            const selected =
                              draft.time?.type === "scheduled" &&
                              draft.time?.date === selectedSlotDate &&
                              draft.time?.start === slot.start &&
                              draft.time?.end === slot.end;
                            const reason = getContextSlotReason(
                              selectedSlotDay,
                              slot,
                              language,
                            );
                            return (
                              <Button
                                aria-pressed={selected}
                                aria-label={
                                  reason ||
                                  formatCheckoutTimeSummary({ date: selectedSlotDate, start: slot.start, end: slot.end }, arabic)
                                }
                                className="context-sheet__slot"
                                disabled={!eligible}
                                key={`${selectedSlotDate}-${slot.start}-${slot.end}`}
                                onClick={() =>
                                  this.selectScheduled(selectedSlotDate, slot)
                                }
                                title={reason}
                                variant="outlined"
                              >
                                <span className="context-sheet__slot-main">
                                  <bdi className="context-sheet__slot-window" dir="ltr">{formatCheckoutTime(slot.start, arabic)} – {formatCheckoutTime(slot.end, arabic)}</bdi>
                                </span>
                                {!eligible && reason && <small>{reason}</small>}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {this.state.error && (
              <div
                className="context-sheet__notice context-sheet__notice--error"
                role="alert"
              >
                <div>
                  <p>{getContextErrorMessage(this.state.preview?.errorDetails?.find(error => error.code === this.state.error) || this.state.error, arabic)}</p>
                  {/[tT]ime|slot|capacity|store_closed|asap_/.test(this.state.error) && (
                    <button type="button" disabled={this.state.slotsLoading} onClick={() => this.loadSlots()}>
                      {arabic ? "تحديث المواعيد" : "Refresh times"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {previewNeedsConfirmation && (
              <section
                className={`context-sheet__review ${
                  previewHasChanges ? "has-changes" : ""
                }`}
              >
                <div className="context-sheet__review-heading">
                  <CheckCircleOutlineIcon />
                  <div>
                    <strong>
                      {arabic
                        ? "تغييرات مطلوبة في السلة"
                        : "A few cart changes are needed"}
                    </strong>
                    <p>
                      {cartChangeSummary(removedCount, adjustedCount, arabic)}
                    </p>
                  </div>
                </div>
                <CartChangePreview preview={preview} cart={this.props.cart} categories={this.props.categories} language={language} />
                <div className="context-sheet__review-rows">
                  {preview.deliveryRate !== null &&
                    preview.deliveryRate !== undefined && (
                      <div>
                        <span>{arabic ? "رسوم التوصيل" : "Delivery fee"}</span>
                        <Money
                          currency={settings.currency_iso}
                          decimals={settings.currency_decimals}
                          displayCurrency={preview.displayCurrency}
                          language={language}
                          value={preview.deliveryRate}
                        />
                      </div>
                    )}
                  {preview.minimumOrderValue !== null &&
                    preview.minimumOrderValue !== undefined && (
                      <div>
                        <span>
                          {arabic ? "الحد الأدنى للطلب" : "Minimum order"}
                        </span>
                        {moneyToMajor(preview.minimumOrderValue) === 0
                          ? arabic ? "لا يوجد حد أدنى" : "No minimum order"
                          : <Money
                          currency={settings.currency_iso}
                          decimals={settings.currency_decimals}
                          displayCurrency={preview.displayCurrency}
                          language={language}
                          value={preview.minimumOrderValue}
                        />}
                      </div>
                    )}
                </div>
              </section>
            )}
          </main>

          <footer className="context-sheet__footer">
            <div className="context-sheet__recap">
              <strong>
                {recap ||
                  (arabic
                    ? "اختر طريقة وموقعاً وموعداً"
                    : "Choose a method, location, and time")}
              </strong>
            </div>
            <div
              className={`context-sheet__footer-actions ${
                dismissible ? "" : "is-required"
              }`}
            >
              {dismissible && (
                <StorefrontActionButton onClick={onClose} tone="quiet">
                  {arabic ? "إلغاء" : "Cancel"}
                </StorefrontActionButton>
              )}
              <StorefrontActionButton
                aria-busy={this.state.loading}
                disabled={
                  !previewNeedsConfirmation &&
                  (!canReview ||
                    this.state.loading ||
                    this.state.optionsLoading)
                }
                endIcon={
                  !previewNeedsConfirmation ? (
                    <ContinueIcon />
                  ) : undefined
                }
                onClick={
                  previewNeedsConfirmation ? this.apply : this.reviewSelection
                }
              >
                <DeferredFeedback
                  active={this.state.loading}
                  fallback={previewNeedsConfirmation
                    ? arabic ? "تطبيق تغييرات السلة" : "Apply cart changes"
                    : actionLabel}
                >
                  <>
                    <CircularProgress size={18} />
                    <span>{arabic ? "جاري التحقق…" : "Checking…"}</span>
                  </>
                </DeferredFeedback>
              </StorefrontActionButton>
            </div>
          </footer>
        </div>
      </Drawer>
    );
  }
}

export const contextSheetMapStateToProps = (state) => {
  const store = state.store?.store || {};
  const settings = store.settings || {};
  return {
    categories: store.categories || [],
    language: state.store?.language || "english",
    settings,
    baseCountry: store.base_country || "",
    baseCountryId: store.base_country_id,
    context: normalizeFulfillmentContext({
      address: state.customers?.address,
      branch: state.branch,
      deliveryTime: state.deliveryTime,
      settings,
    }),
    cart: state.cart || {},
    checkoutDraft: state.checkoutV2?.draft || {},
    countryCode: store.country_code || state.currency?.selected || null,
    countries:
      Array.isArray(store.all_countries) && store.all_countries.length
        ? store.all_countries
        : store.countries || [],
    areas: store.areas || [],
    branches: sortContextBranches(
      store.branches || [],
      state.store?.language || "english",
    ),
    branchDeliveryCharges: store.branch_delivery_charges || [],
  };
};

export const contextSheetMapDispatchToProps = (dispatch) => ({
  commitContext: (context) => dispatch(commitCheckoutV2Context(context)),
  setValidation: (validation) =>
    dispatch(setCheckoutV2CartValidation(validation)),
  fetchContextOptions: (countryCode) =>
    // Opening F1 is a read-only hydration step. Legacy map-pin reconciliation
    // can mutate the committed branch/address and must remain behind Preview +
    // Apply for checkout v2.
    dispatch(fetchDeliveryCharges(countryCode, { validateMapPin: false })),
  loadBranchDetails: (branchId) => dispatch(loadBranchDetails(branchId)),
});

export default connect(
  contextSheetMapStateToProps,
  contextSheetMapDispatchToProps,
)(ContextSheet);
